/**
 * Additional SDI hooks for P7-P12 features.
 * APPEND to the existing useSDI.ts or import alongside.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/shared/lib/api'
import type { ApiEnvelope, JobRunItem } from './sdi.types'

// ── Dashboard ────────────────────────────────────────────────

export interface DashboardData {
  active_markets: number
  total_market_size: number
  avg_capture_rate: number | null
  total_lost_value: number
  total_budget: number
  avg_confidence: number | null
  status_breakdown: Record<string, number>
  markets: Array<{
    id: string
    name: string
    country: string
    status: string
    market_size: number
    capture_rate: number
    lost_value: number
    confidence_score: number
    monthly_budget: number
    last_sync: string | null
  }>
}

export const useDashboard = (params?: { country?: string; date_from?: string; date_to?: string }) =>
  useQuery({
    queryKey: ['sdi', 'dashboard', params],
    queryFn: () =>
      api.get<ApiEnvelope<DashboardData>>('/sdi/dashboard', { params }).then((r) => r.data.data),
  })

// ── Global Jobs Feed ─────────────────────────────────────────

export const useRecentJobsGlobal = (limit?: number) =>
  useQuery({
    queryKey: ['sdi', 'jobs', 'global', limit],
    queryFn: () =>
      api.get<ApiEnvelope<JobRunItem[]>>('/sdi/jobs/recent', {
        params: limit ? { limit } : undefined,
      }).then((r) => r.data.data),
    refetchInterval: 60_000, // auto-refresh every 60s
  })

// ── Delete Market ────────────────────────────────────────────

export const useDeleteMarket = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (marketId: string) =>
      api.delete(`/sdi/markets/${marketId}`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sdi'] }),
  })
}
